interface Props {
  slackResult: string | null;   // "connected" | "error" | null
  slackTeam: string | null;
  slackReason: string | null;
  onTryAgain: () => void;
  onDismiss: () => void;
}

interface Msg {
  icon: string;
  text: string;
  showTryAgain: boolean;
  tone: "ok" | "warn" | "danger" | "info";
}

function messageFor(result: string | null, reason: string | null, team: string | null): Msg | null {
  if (result === "connected") {
    return {
      icon: "✅",
      text: `${team ? `${team} connected. ` : "Slack workspace connected. "}Next step: invite the bot into a channel and add a channel config below.`,
      showTryAgain: false,
      tone: "ok",
    };
  }
  if (result !== "error") return null;
  switch (reason) {
    case "access_denied":
      return { icon: "ⓘ", tone: "info", showTryAgain: true,
        text: "You cancelled the Slack install. No workspace was connected." };
    case "bad_state":
      return { icon: "⚠️", tone: "warn", showTryAgain: true,
        text: "Your install link expired. Please start over." };
    case "oauth_failed":
      return { icon: "⚠️", tone: "warn", showTryAgain: true,
        text: "Slack rejected the install. This is usually temporary — please retry." };
    case "workspace_already_connected":
      return { icon: "🚫", tone: "danger", showTryAgain: false,
        text: "This Slack workspace is already connected to a different Daybrief account. Contact your admin to have them disconnect it first." };
    default:
      return { icon: "⚠️", tone: "warn", showTryAgain: true,
        text: `Slack returned an unexpected error: ${reason ?? "unknown"}. Please try again or contact support.` };
  }
}

const toneClass: Record<Msg["tone"], string> = {
  ok:     "border-border-subtle bg-surface-raised",
  info:   "border-border-subtle bg-surface-raised",
  warn:   "border-warn text-ink-800",
  danger: "border-danger-text text-danger-text",
};

export function SlackBanner(props: Props) {
  const msg = messageFor(props.slackResult, props.slackReason, props.slackTeam);
  if (!msg) return null;
  return (
    <div className={`text-sm rounded-md px-4 py-3 border flex items-start gap-3 ${toneClass[msg.tone]}`}>
      <span aria-hidden>{msg.icon}</span>
      <span className="flex-1">{msg.text}</span>
      {msg.showTryAgain && (
        <button onClick={props.onTryAgain} className="text-xs underline">Try again</button>
      )}
      <button onClick={props.onDismiss} className="text-xs underline">Dismiss</button>
    </div>
  );
}

export { messageFor };
